import React, { useState } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { WhatsAppButton } from '../components/WhatsAppButton';

export const ContactPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    destination: '',
    country: '',
    phone: '',
    travelDates: '',
    groupSize: '',
    interests: [],
    budget: '',
    message: ''
  });

  const interestOptions = [
    'Cultural & Heritage',
    'Coffee Heritage Trail',
    'Gastronomic Experiences',
    'Art & Design',
    'Adventure & Nature',
    'Luxury Accommodation',
    'Photography',
    'Wellness & Spa'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inquiries/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: "Inquiry Received",
          description: data.message || "Thank you for your interest. We'll contact you within 24 hours.",
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          destination: '',
          country: '',
          phone: '',
          travelDates: '',
          groupSize: '',
          interests: [],
          budget: '',
          message: ''
        });
      } else {
        toast({
          title: "Submission Failed",
          description: data.detail || "Please try again or contact us directly.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Connection Error",
        description: "Unable to submit inquiry. Please check your connection and try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div style={{ background: 'linear-gradient(135deg, #070D1E 0%, #0B132B 50%, #0F172A 100%)', minHeight: '100vh' }}>
      <Navigation />
      <Toaster />
      <WhatsAppButton variant="floating" />
      
      {/* Hero Section */}
      <section style={{
        padding: '120px 0 80px',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 style={{
            fontSize: '42px',
            fontWeight: 300,
            fontFamily: 'Georgia, serif',
            color: '#FBBF24',
            marginBottom: '16px',
            letterSpacing: '1px'
          }}>
            Plan Your Journey
          </h1>
          <p style={{ 
            fontSize: '17px',
            color: '#E2E8F0', 
            maxWidth: '700px', 
            margin: '0 auto',
            lineHeight: 1.7
          }}>
            Share your vision, and we&apos;ll craft a bespoke itinerary that reflects your passions and preferences.
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section style={{ padding: '60px 0 100px' }}>
        <div className="container">
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(251, 191, 36, 0.2)',
            borderRadius: '24px',
            padding: '48px 40px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
          }}>
            {/* WhatsApp Direct Inquiry */}
            <div style={{
              background: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '16px',
              padding: '20px 24px',
              marginBottom: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div>
                <p style={{ 
                  fontSize: '15px', 
                  fontWeight: 600, 
                  color: '#FBBF24',
                  marginBottom: '4px'
                }}>
                  Need Immediate Assistance?
                </p>
                <p style={{ fontSize: '13px', color: '#CBD5E1' }}>
                  Chat with our travel designers on WhatsApp
                </p>
              </div>
              <a
                href="https://wa.me/6281517118799?text=Hello%20Indotravelista,%20I%20would%20like%20to%20plan%20a%20bespoke%20journey."
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #FBBF24 0%, #D4AF37 100%)',
                  color: '#070D1E',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: 600,
                  borderRadius: '9999px',
                  transition: 'all 0.3s',
                  boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)'
                }}
                className="whatsapp-cta"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                WhatsApp Us
              </a>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Journey Selection */}
              <div style={{ marginBottom: '48px' }}>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: 300,
                  fontFamily: 'Georgia, serif',
                  color: '#FBBF24',
                  marginBottom: '24px',
                  letterSpacing: '0.5px'
                }}>
                  Your Desired Journey
                </h2>
                
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px',
                    fontSize: '14px',
                    color: '#E2E8F0',
                    fontWeight: 500
                  }}>
                    Destination / Journey Type *
                  </label>
                  <select
                    name="destination"
                    value={formData.destination}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      background: 'rgba(2, 6, 23, 0.8)',
                      border: '1px solid rgba(148, 163, 184, 0.3)',
                      borderRadius: '12px',
                      color: '#F8FAFC',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    className="form-select"
                  >
                    <option value="">Select your desired journey...</option>
                    <optgroup label="━━━ INDONESIA JOURNEYS ━━━">
                      <option value="Bali Luxury & Cultural Retreat">Bali Luxury & Cultural Retreat</option>
                      <option value="Indonesia Coffee Civilization & Heritage Trail">Indonesia Coffee Civilization & Heritage Trail</option>
                      <option value="Komodo & Flores Liveaboard Odyssey">Komodo & Flores Liveaboard Odyssey</option>
                      <option value="Customized Indonesia Bespoke Journey">Customized Indonesia Bespoke Journey</option>
                    </optgroup>
                    <optgroup label="━━━ WORLDWIDE SIGNATURE JOURNEYS ━━━">
                      <option value="7D6N Nepal Spiritual & Scenic Journey (Dec 2026)">7D6N Nepal Spiritual & Scenic Journey (Dec 2026)</option>
                      <option value="7-Day Bhutan Druk Wangyel Festival (Dec 2026)">7-Day Bhutan Druk Wangyel Festival (Dec 2026)</option>
                      <option value="Shanghai & Royal Caribbean Cruise (March 2027)">Shanghai & Royal Caribbean Cruise (March 2027)</option>
                      <option value="Other Worldwide Custom Journey">Other Worldwide Custom Journey</option>
                    </optgroup>
                    <optgroup label="━━━ CORPORATE & MICE ━━━">
                      <option value="Corporate Incentive / Executive Gathering / MICE">Corporate Incentive / Executive Gathering / MICE</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              {/* Personal Information */}
              <div style={{ marginBottom: '48px' }}>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: 300,
                  fontFamily: 'Georgia, serif',
                  color: '#FBBF24',
                  marginBottom: '24px',
                  letterSpacing: '0.5px'
                }}>
                  Personal Information
                </h2>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '24px',
                  marginBottom: '24px'
                }} className="form-grid">
                  <div>
                    <label className="body-regular" style={{ display: 'block', marginBottom: '8px' }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '16px',
                        border: '1px solid var(--border-medium)',
                        borderRadius: 0,
                        fontSize: '14px',
                        background: 'var(--bg-primary)',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--interactive-base)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                    />
                  </div>
                  <div>
                    <label className="body-regular" style={{ display: 'block', marginBottom: '8px' }}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '16px',
                        border: '1px solid var(--border-medium)',
                        borderRadius: 0,
                        fontSize: '14px',
                        background: 'var(--bg-primary)',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--interactive-base)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                    />
                  </div>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '24px'
                }} className="form-grid">
                  <div>
                    <label className="body-regular" style={{ display: 'block', marginBottom: '8px' }}>
                      Country of Residence
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '16px',
                        border: '1px solid var(--border-medium)',
                        borderRadius: 0,
                        fontSize: '14px',
                        background: 'var(--bg-primary)',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--interactive-base)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                    />
                  </div>
                  <div>
                    <label className="body-regular" style={{ display: 'block', marginBottom: '8px' }}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '16px',
                        border: '1px solid var(--border-medium)',
                        borderRadius: 0,
                        fontSize: '14px',
                        background: 'var(--bg-primary)',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--interactive-base)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                    />
                  </div>
                </div>
              </div>

              {/* Travel Details */}
              <div style={{ marginBottom: '48px' }}>
                <h2 className="heading-2" style={{ marginBottom: '24px' }}>
                  Travel Details
                </h2>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '24px',
                  marginBottom: '24px'
                }} className="form-grid">
                  <div>
                    <label className="body-regular" style={{ display: 'block', marginBottom: '8px' }}>
                      Preferred Travel Dates
                    </label>
                    <input
                      type="text"
                      name="travelDates"
                      value={formData.travelDates}
                      onChange={handleInputChange}
                      placeholder="e.g., March 2025"
                      style={{
                        width: '100%',
                        padding: '16px',
                        border: '1px solid var(--border-medium)',
                        borderRadius: 0,
                        fontSize: '14px',
                        background: 'var(--bg-primary)',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--interactive-base)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                    />
                  </div>
                  <div>
                    <label className="body-regular" style={{ display: 'block', marginBottom: '8px' }}>
                      Group Size
                    </label>
                    <select
                      name="groupSize"
                      value={formData.groupSize}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '16px',
                        border: '1px solid var(--border-medium)',
                        borderRadius: 0,
                        fontSize: '14px',
                        background: 'var(--bg-primary)',
                        transition: 'border-color 0.2s',
                        cursor: 'pointer'
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--interactive-base)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                    >
                      <option value="">Select group size</option>
                      <option value="1-2">1-2 travelers</option>
                      <option value="3-5">3-5 travelers</option>
                      <option value="6-10">6-10 travelers</option>
                      <option value="10+">10+ travelers</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '12px' }}>
                    Travel Interests (Select all that apply)
                  </label>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '12px'
                  }}>
                    {interestOptions.map((interest) => (
                      <label
                        key={interest}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 16px',
                          border: `1px solid ${formData.interests.includes(interest) ? 'var(--interactive-base)' : 'var(--border-medium)'}`,
                          background: formData.interests.includes(interest) ? 'var(--bg-secondary)' : 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          borderRadius: 0
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.interests.includes(interest)}
                          onChange={() => handleInterestToggle(interest)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span className="body-small">{interest}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px' }}>
                    Estimated Budget per Person (USD)
                  </label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: 0,
                      fontSize: '14px',
                      background: 'var(--bg-primary)',
                      transition: 'border-color 0.2s',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--interactive-base)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                  >
                    <option value="">Select budget range</option>
                    <option value="under-1000">Under US$1,000</option>
                    <option value="1000-2500">US$1,000 – US$2,500</option>
                    <option value="2500-5000">US$2,500 – US$5,000</option>
                    <option value="5000-7500">US$5,000 – US$7,500</option>
                    <option value="7500-10000">US$7,500 – US$10,000</option>
                    <option value="10000+">US$10,000+</option>
                  </select>
                </div>
              </div>

              {/* Additional Information */}
              <div style={{ marginBottom: '48px' }}>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px' }}>
                  Tell us about your ideal journey
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="6"
                  placeholder="Share any specific requests, dietary requirements, accessibility needs, or special occasions you're celebrating..."
                  style={{
                    width: '100%',
                    padding: '16px',
                    border: '1px solid var(--border-medium)',
                    borderRadius: 0,
                    fontSize: '14px',
                    background: 'var(--bg-primary)',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--interactive-base)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                />
              </div>

              <button type="submit" className="btn-primary" style={{ width: '100%' }}>
                Submit Inquiry
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};
